import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';
import { AccessState } from '../../../../types';
import {
  BusRoute,
  BusRoutesCampus,
  BusRouteWithTours,
  BusScheduleDetail,
  CreateGroupedTours,
  BusSchedule,
  BusScheduleDetailPayloadNew,
  BusRouteSchedulesDetails,
  CreateSchuduleDetailsPayload,
} from '../interfaces/buses.interfaces';
import { IResponse } from '../../../shared/interfaces/response.interface';
import { CustomChilds } from '../../../shared/interfaces/material-ui.interfaces';
import { Campus } from '../../campus/interfaces/campus.interfaces';

export const busRouteTags = middlewareApi.enhanceEndpoints({
  addTagTypes: [
    'BusRoute',
    'BusSchedules',
    'BusRouteSchedules',
    'BusRouteTours',
    'BusSchedulesDetails',
  ],
});
// Define a service using a base URL and expected endpoints
export const busRouteApi = busRouteTags.injectEndpoints({
  // overrideExisting: false,

  endpoints: (builder) => ({
    /**
     * @GET routes by campus
     */
    getRoutesCampus: builder.query<BusRoutesCampus[], { profileId?: number }>({
      queryFn: async ({ profileId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<BusRoutesCampus[]>(`/bus-routes-campus`, {
            params: {
              user_profile_id,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'BusRoute', id } as const)),
              { type: 'BusRoute', id: 'LIST' },
            ]
          : [{ type: 'BusRoute', id: 'LIST' }],
    }),

    /**
     * @GET Horarios de la ruta
     */
    getRoutesSchedules: builder.query<
      BusRouteSchedulesDetails[],
      { profileId?: number; route: number }
    >({
      queryFn: async ({ profileId, route }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<BusRouteSchedulesDetails[]>(
            `/bus-routes/${route}/schedules`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'BusRouteSchedules', id: 'LIST' }],
    }),

    /**
     * @GET bus route by id and tours childs
     */
    getRouteTours: builder.query<BusRouteWithTours, { profileId?: number; route: number }>({
      queryFn: async ({ profileId, route }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<BusRouteWithTours>(`/bus-routes/${route}/tours`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'BusRouteTours', id: 'LIST' }],
    }),

    /**
     * @GET all routes
     */
    getRoutes: builder.query<
      IResponse<CustomChilds<BusRoute, { campus: any }>>,
      {
        profileId?: number;
        page?: number;
        perPage?: number;
        search?: string;
        campusId?: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async (
        { profileId, page, search, perPage, campusId, sort, type_sort },
        { dispatch, getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;
          const { data } = await mainApi.get<IResponse<CustomChilds<BusRoute, { campus: Campus }>>>(
            '/bus-routes',
            {
              params: {
                user_profile_id,
                page,
                size: perPage,
                // sort: 'students.id',
                type_sort: type_sort || 'desc',
                ...(sort ? { sort } : { sort: 'id' }),
                ...((Number(campusId) || 0) > 0 && { campus_id: campusId }),
                search,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ campus_id }) => ({ type: 'BusRoute', campus_id } as const)),
              { type: 'BusRoute', id: 'LIST' },
            ]
          : [{ type: 'BusRoute', id: 'LIST' }],
    }),
    /**
     * @GET routes by id
     */
    getRoute: builder.query<
      CustomChilds<BusRoute, { campus: Campus }>,
      {
        profileId?: number;
        routeId: number;
      }
    >({
      queryFn: async ({ profileId, routeId }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;
          const { data } = await mainApi.get<CustomChilds<BusRoute, { campus: Campus }>>(
            `/bus-routes/${routeId}`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result ? [{ type: 'BusRoute', id: 'LIST' }] : [{ type: 'BusRoute', id: 'LIST' }],
    }),

    /**
     * @POST create new route
     */
    createBusRoute: builder.mutation<
      BusRoute,
      { profileId?: number; dataPayload: Omit<BusRoute, 'id' | 'status_id'> }
    >({
      queryFn: async ({ profileId, dataPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.post<BusRoute>('/bus-routes', {
            user_profile_id,
            ...dataPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { dataPayload }) => [
        { type: 'BusRoute', id: 'LIST' },
        { type: 'BusRouteSchedules', id: dataPayload.campus_id },
      ],
    }),

    /**
     * @PUT update new route
     */
    updateBusRoute: builder.mutation<
      BusRoute,
      { profileId?: number; routeId: number; dataPayload: Omit<BusRoute, 'id'> }
    >({
      queryFn: async ({ profileId, routeId, dataPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.put<BusRoute>(`/bus-routes/${routeId}`, {
            user_profile_id,
            ...dataPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { dataPayload }) => [
        { type: 'BusRoute', id: 'LIST' },
        { type: 'BusRouteSchedules', id: dataPayload.campus_id },
      ],
    }),

    /**
     * @DELETE delete route
     */
    deleteBusRoute: builder.mutation<BusRoute, { profileId?: number; routeId: number }>({
      queryFn: async ({ profileId, routeId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.delete<BusRoute>(`/bus-routes/${routeId}`, {
            data: {
              user_profile_id,
            },
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error) => [
        { type: 'BusRoute', id: 'LIST' },
        { type: 'BusRouteSchedules', id: 'LIST' },
      ],
    }),

    /**
     * @POST crear parada (crud normal)
     */
    createBusSchedule: builder.mutation<
      any,
      { profileId?: number; dataPayload: BusScheduleDetailPayloadNew }
    >({
      queryFn: async ({ profileId, dataPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.post<any>(`/bus-schedules`, {
            user_profile_id,
            ...dataPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { dataPayload }) => [{ type: 'BusSchedules', id: 'LIST' }],
    }),

    /**
     * @PUT crear parada (crud normal)
     */
    saveSchedulesOnRoutes: builder.mutation<
      any,
      { profileId?: number; routeId: number; selection: number[] }
    >({
      queryFn: async ({ profileId, routeId, selection }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.put<any>(`/bus-schedules-assign`, {
            user_profile_id,
            route_id: routeId,
            schedules: [...selection],
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error) => [
        { type: 'BusSchedules', id: 'LIST' },
        { type: 'BusRouteSchedules', id: 'LIST' },
      ],
    }),

    /**
     * @PUT update grupo de paradas
     */
    updateBusToursGrouped: builder.mutation<
      any,
      { profileId?: number; dataPayload: CreateGroupedTours }
    >({
      queryFn: async ({ profileId, dataPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.put<any>(`/bus-tours-grouped`, {
            user_profile_id,
            ...dataPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },

      // invalidatesTags: (result, error, { dataPayload }) => [
      //   { type: 'BusRouteTours', id: 'LIST' },
      // ],
    }),

    /**
     * @GET Schedules table
     */
    getBusesSchedules: builder.query<
      IResponse<BusSchedule>,
      {
        profileId?: number;
        page: number;
        perPage: number;
        search: string;
        sort?: string;
        typeSort?: string;
      }
    >({
      queryFn: async (
        { profileId, page, perPage, search, sort = 'id', typeSort = 'desc' },
        { getState }
      ) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = profileId || currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<IResponse<BusSchedule>>(`/bus-schedules`, {
            params: {
              user_profile_id,
              search,
              page,
              size: perPage,
              sort,
              type_sort: typeSort,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) =>
        result
          ? [
              ...result.data.map(({ id }: any) => ({ type: 'BusSchedules', id } as const)),
              { type: 'BusSchedules', id: 'LIST' },
            ]
          : [{ type: 'BusSchedules', id: 'LIST' }],
    }),

    /**
     * @GET schedules with details
     */
    getBusSchedulesWithDetails: builder.query<BusRouteSchedulesDetails[], { profileId?: number }>({
      queryFn: async ({ profileId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = profileId || currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<BusRouteSchedulesDetails[]>(`/bus-schedules-details`, {
            params: {
              user_profile_id,
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'BusSchedules', id: 'LIST' }],
    }),
    //

    /**
     * @GET schedule bus by id
     */
    getBusSchedule: builder.query<BusSchedule, { profileId?: number; scheduleId: number }>({
      queryFn: async ({ profileId, scheduleId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = profileId || currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<BusSchedule>(`/bus-schedules/${scheduleId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [
        { type: 'BusSchedules', id: 'LIST' },
        { type: 'BusSchedules', id: result?.id },
      ],
    }),

    /**
     * @DELETE delete schedule
     */
    deleteSchedule: builder.mutation<BusRoute, { profileId?: number; scheduleId: number }>({
      queryFn: async ({ profileId, scheduleId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.delete<BusRoute>(`/bus-schedules/${scheduleId}`, {
            data: {
              user_profile_id,
            },
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { scheduleId }) => [
        { type: 'BusSchedules', id: scheduleId },
      ],
    }),

    /**
     * @GET detalles del horario
     */
    getScheduleDetails: builder.query<
      BusScheduleDetail[],
      { profileId?: number; scheduleId: number }
    >({
      queryFn: async ({ profileId, scheduleId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<BusScheduleDetail[]>(
            `/bus-schedules/${scheduleId}/details`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { scheduleId }) => [
        { type: 'BusSchedules', id: 'LIST' },
        { type: 'BusSchedules', id: scheduleId },
      ],
    }),

    /**
     * @PUT update detalles de horario
     */
    updateScheduleDetailsGrouped: builder.mutation<
      any,
      { profileId?: number; dataPayload: CreateSchuduleDetailsPayload }
    >({
      queryFn: async ({ profileId, dataPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.put<any>(`/bus-schedule-details-grouped`, {
            user_profile_id,
            ...dataPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { dataPayload }) => [
        { type: 'BusSchedulesDetails', id: 'LIST' },
        { type: 'BusSchedules', id: dataPayload.id || 'LIST' },
        // invalidar todos los tagsd con los id de los detalles update
        // ...result.data.map(({ id }: any) => ({ type: 'BusSchedulesDetails', id } as const)),
      ],
    }),

    /**
     * @DELETE delete schedule detail
     */
    deleteScheduleDetail: builder.mutation<
      BusRoute,
      { profileId?: number; scheduleDetailId: number }
    >({
      queryFn: async ({ profileId, scheduleDetailId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.delete<BusRoute>(
            `/bus-schedule-details/${scheduleDetailId}`,
            {
              data: {
                user_profile_id,
              },
            }
          );
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { scheduleDetailId }) => [
        { type: 'BusSchedulesDetails', id: scheduleDetailId },
      ],
    }),

    //
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetRoutesCampusQuery,
  useGetRoutesSchedulesQuery,
  useGetRoutesQuery,
  useCreateBusRouteMutation,
  useUpdateBusRouteMutation,
  useDeleteBusRouteMutation,
  useGetRouteToursQuery,
  useUpdateBusToursGroupedMutation,
  useCreateBusScheduleMutation,
  useGetScheduleDetailsQuery,
  useDeleteScheduleDetailMutation,
  useUpdateScheduleDetailsGroupedMutation,
  useDeleteScheduleMutation,
  useGetBusesSchedulesQuery,
  useLazyGetBusScheduleQuery,
  useGetBusScheduleQuery,
  useGetBusSchedulesWithDetailsQuery,
  useSaveSchedulesOnRoutesMutation,
  useGetRouteQuery,
  useLazyGetRouteQuery,
} = busRouteApi;
